import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  BookOpen, 
  Globe, 
  FileText,
  BookMarked,
  Filter,
  Copy,
  ChevronDown,
  ExternalLink,
  Loader2,
  AlertCircle,
  CheckCircle,
  Info,
  Sparkles,
  Download,
  Eye,
  TriangleAlert
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const sourceTypeIcons = {
  WEBSITE: Globe,
  BOOK: BookMarked,
  JOURNAL: FileText,
  PDF: FileText,
  OTHER: BookOpen,
};

const sourceTypeLabels = {
  WEBSITE: 'Website',
  BOOK: 'Book',
  JOURNAL: 'Journal',
  PDF: 'PDF',
  OTHER: 'Other',
};

const qualityIndicatorIcons = {
  warning: AlertCircle,
  success: CheckCircle,
  info: Info,
};

const ResearchPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tags, setTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState({ type: 'all', project: 'all', status: 'all' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('library');
  
  // Add source form
  const [inputValue, setInputValue] = useState('');
  const [fetchingMetadata, setFetchingMetadata] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    authors: '',
    publisher: '',
    publishedDate: '',
    canonicalUrl: '',
    sourceType: 'WEBSITE',
    projectId: ''
  });

  // Source browser
  const [browserQuery, setBrowserQuery] = useState('');
  const [browserResults, setBrowserResults] = useState([]);
  const [browserLoading, setBrowserLoading] = useState(false);
  const [browserType, setBrowserType] = useState('all');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.status]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status !== 'all') params.append('filter', filter.status);
      
      const [sourcesRes, projectsRes, tagsRes] = await Promise.all([
        api.get(`/sources?${params.toString()}`),
        api.get('/projects'),
        api.get('/tags')
      ]);
      setSources(sourcesRes.data.sources || []);
      setDuplicates(sourcesRes.data.duplicates || []);
      setProjects(projectsRes.data.projects || []);
      setTags(tagsRes.data.tags || []);
    } catch (err) {
      toast.error('Failed to load research library');
    } finally {
      setLoading(false);
    }
  };

  const handleFetchMetadata = async () => {
    if (!inputValue.trim()) {
      toast.error('Please enter a URL, DOI, ISBN, or title');
      return;
    }

    setFetchingMetadata(true);
    try {
      const res = await api.post('/sources/fetch-metadata', { input: inputValue });
      setMetadata(res.data);
      
      const meta = res.data.metadata || {};
      setFormData({
        title: meta.title || inputValue,
        authors: Array.isArray(meta.authors) ? meta.authors.join(', ') : '',
        publisher: meta.publisher || '',
        publishedDate: meta.publishedDate || '',
        canonicalUrl: res.data.canonicalUrl || (res.data.inputType === 'url' ? inputValue : ''),
        sourceType: meta.sourceType || 'WEBSITE',
        doi: meta.doi || '',
        isbn: meta.isbn || '',
        projectId: ''
      });
      
      toast.success(`Metadata ${res.data.confidence === 'high' ? 'found' : 'partially found'}`);
    } catch (err) {
      toast.error('Failed to fetch metadata');
      setFormData({
        ...formData,
        title: inputValue,
        canonicalUrl: inputValue.startsWith('http') ? inputValue : ''
      });
    } finally {
      setFetchingMetadata(false);
    }
  };

  const handleCreateSource = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    try {
      const authors = formData.authors
        ? formData.authors.split(',').map(a => a.trim()).filter(Boolean)
        : [];

      const res = await api.post('/sources', {
        ...formData,
        authors,
        projectId: formData.projectId && formData.projectId !== 'none' ? formData.projectId : null
      });

      setSources([res.data.source, ...sources]);
      setDialogOpen(false);
      resetForm();
      toast.success('Source added!');
      navigate(`/research/${res.data.source.id}`);
    } catch (err) {
      if (err.response?.data?.existingId) {
        toast.error('This source already exists');
        navigate(`/research/${err.response.data.existingId}`);
      } else {
        toast.error(err.response?.data?.error || 'Failed to add source');
      }
    }
  };

  const resetForm = () => {
    setInputValue('');
    setMetadata(null);
    setFormData({
      title: '',
      authors: '',
      publisher: '',
      publishedDate: '',
      canonicalUrl: '',
      sourceType: 'WEBSITE',
      projectId: ''
    });
  };

  const handleCopyCitation = async (source, style) => {
    try {
      const res = await api.get(`/sources/${source.id}/citation/${style}`);
      await navigator.clipboard.writeText(res.data.bibliography);
      toast.success(`${style.toUpperCase()} citation copied!`);
    } catch (err) {
      toast.error('Failed to copy citation');
    }
  };

  // Source Browser functions
  const handleBrowserSearch = async () => {
    if (!browserQuery.trim() || browserQuery.length < 3) {
      toast.error('Please enter at least 3 characters');
      return;
    }

    setBrowserLoading(true);
    try {
      const res = await api.get(`/source-browser/search?query=${encodeURIComponent(browserQuery)}&type=${browserType}`);
      setBrowserResults(res.data.results || []);
      if (res.data.results?.length === 0) {
        toast.info('No results found. Try different keywords.');
      }
    } catch (err) {
      toast.error('Search failed');
    } finally {
      setBrowserLoading(false);
    }
  };

  const handleSaveFromBrowser = async (result) => {
    try {
      const res = await api.post('/source-browser/save', {
        ...result,
        projectId: filter.project !== 'all' ? filter.project : null
      });
      setSources([res.data.source, ...sources]);
      toast.success('Source saved to library!');
    } catch (err) {
      if (err.response?.data?.existingId) {
        toast.info('Source already in your library');
      } else {
        toast.error('Failed to save source');
      }
    }
  };

  const filteredSources = sources.filter(s => {
    if (filter.type !== 'all' && s.sourceType !== filter.type) return false;
    if (filter.project !== 'all') {
      const hasProject = s.projectSources?.some(ps => ps.projectId === filter.project);
      if (!hasProject) return false;
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        s.title?.toLowerCase().includes(query) ||
        s.authors?.some(a => a.toLowerCase().includes(query)) ||
        s.publisher?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Stats
  const stats = {
    total: sources.length,
    used: sources.filter(s => s.isUsed).length,
    unused: sources.filter(s => !s.isUsed).length,
    incomplete: sources.filter(s => !s.bibliographyComplete?.complete).length,
    openAccess: sources.filter(s => s.isOpenAccess).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm text-muted-foreground font-mono animate-pulse">Loading research library...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto animate-fade-in" data-testid="research-page">
      {/* Header */}
      <div className="flex items-start justify-between border-b border-border pb-5 mb-6">
        <div>
          <p className="label-swiss mb-1">Sources</p>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Research Library</h1>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="add-source-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Add Source
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Source</DialogTitle>
            </DialogHeader>
            
            <Tabs defaultValue="auto" className="mt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="auto">Auto-detect</TabsTrigger>
                <TabsTrigger value="manual">Manual Entry</TabsTrigger>
              </TabsList>
              
              <TabsContent value="auto" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Paste URL, DOI, ISBN, or Title</Label>
                  <div className="flex gap-2">
                    <Input
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder="https://example.com, 10.1234/..., or book title"
                      data-testid="source-input"
                    />
                    <Button 
                      type="button" 
                      onClick={handleFetchMetadata}
                      disabled={fetchingMetadata}
                      data-testid="fetch-metadata-btn"
                    >
                      {fetchingMetadata ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Fetch'}
                    </Button>
                  </div>
                  {metadata && (
                    <div className="text-sm">
                      <Badge variant="outline" className={metadata.confidence === 'high' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}>
                        {metadata.confidence === 'high' ? 'High confidence' : 'Low confidence - please verify'}
                      </Badge>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="manual" className="mt-4" />
            </Tabs>

            <form onSubmit={handleCreateSource} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Source title"
                  data-testid="source-title-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="authors">Author(s)</Label>
                  <Input
                    id="authors"
                    value={formData.authors}
                    onChange={(e) => setFormData({ ...formData, authors: e.target.value })}
                    placeholder="John Smith, Jane Doe"
                    data-testid="source-authors-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Source Type</Label>
                  <Select 
                    value={formData.sourceType} 
                    onValueChange={(v) => setFormData({ ...formData, sourceType: v })}
                  >
                    <SelectTrigger data-testid="source-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(sourceTypeLabels).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="publisher">Publisher / Website</Label>
                  <Input
                    id="publisher"
                    value={formData.publisher}
                    onChange={(e) => setFormData({ ...formData, publisher: e.target.value })}
                    placeholder="Publisher name"
                    data-testid="source-publisher-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="publishedDate">Published Date</Label>
                  <Input
                    id="publishedDate"
                    value={formData.publishedDate}
                    onChange={(e) => setFormData({ ...formData, publishedDate: e.target.value })}
                    placeholder="2024 or 2024-01-15"
                    data-testid="source-date-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="canonicalUrl">URL</Label>
                <Input
                  id="canonicalUrl"
                  type="url"
                  value={formData.canonicalUrl}
                  onChange={(e) => setFormData({ ...formData, canonicalUrl: e.target.value })}
                  placeholder="https://..."
                  data-testid="source-url-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Link to Project</Label>
                <Select 
                  value={formData.projectId} 
                  onValueChange={(v) => setFormData({ ...formData, projectId: v })}
                >
                  <SelectTrigger data-testid="source-project-select">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.title || p.subject || p.type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="source-submit-btn">
                  Add Source
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-5 border border-border mb-6">
        {[
          { label: 'Total', value: stats.total, key: 'all' },
          { label: 'Used', value: stats.used, key: 'used' },
          { label: 'Unused', value: stats.unused, key: 'unused' },
          { label: 'Incomplete', value: stats.incomplete, key: 'incomplete' },
          { label: 'Open Access', value: stats.openAccess, key: null },
        ].map((s, i) => (
          <button
            key={s.label}
            onClick={() => s.key && setFilter({ ...filter, status: s.key })}
            className={`py-4 px-3 text-left transition-colors ${i > 0 ? 'border-l border-border' : ''} ${
              s.key && filter.status === s.key ? 'bg-foreground' : s.key ? 'hover:bg-muted/50' : 'cursor-default'
            }`}
          >
            <p className={`text-xl font-bold font-mono ${s.key && filter.status === s.key ? 'text-white' : 'text-foreground'}`}>
              {s.value}
            </p>
            <p className={`text-[10px] uppercase tracking-[0.12em] font-semibold mt-0.5 ${s.key && filter.status === s.key ? 'text-white/60' : 'text-muted-foreground'}`}>
              {s.label}
            </p>
          </button>
        ))}
      </div>

      {/* Duplicate Warning */}
      {duplicates.length > 0 && (
        <Alert className="mb-6 border-amber-200 bg-amber-50">
          <TriangleAlert className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            {duplicates.length} potential duplicate source(s) detected. Review your library for redundant entries.
          </AlertDescription>
        </Alert>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="library" className="gap-2">
            <BookOpen className="w-4 h-4" />
            My Library
          </TabsTrigger>
          <TabsTrigger value="browser" className="gap-2">
            <Sparkles className="w-4 h-4" />
            Find Sources
          </TabsTrigger>
        </TabsList>

        {/* Library Tab */}
        <TabsContent value="library">
          {/* Search & Filters */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search sources..."
                className="pl-10"
                data-testid="search-sources-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="filter-source-type-btn">
                    <Filter className="w-4 h-4 mr-2" />
                    {filter.type === 'all' ? 'All Types' : sourceTypeLabels[filter.type]}
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => setFilter({ ...filter, type: 'all' })}>All Types</DropdownMenuItem>
                  {Object.entries(sourceTypeLabels).map(([value, label]) => (
                    <DropdownMenuItem key={value} onClick={() => setFilter({ ...filter, type: value })}>
                      {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="filter-project-btn">
                    {filter.project === 'all' ? 'All Projects' : 'Project'}
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => setFilter({ ...filter, project: 'all' })}>All Projects</DropdownMenuItem>
                  {projects.map((p) => (
                    <DropdownMenuItem key={p.id} onClick={() => setFilter({ ...filter, project: p.id })}>
                      {p.title || p.subject || p.type}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Sources List */}
          {filteredSources.length === 0 ? (
            <div className="border border-border py-16 text-center">
              <BookOpen className="w-8 h-8 mx-auto mb-3 text-muted-foreground/40" />
              <h3 className="font-semibold text-foreground mb-1">
                {sources.length === 0 ? 'No sources yet' : 'No matching sources'}
              </h3>
              <p className="text-sm text-muted-foreground mb-5">
                {sources.length === 0
                  ? 'Add your first source or search for open access materials'
                  : 'Try adjusting your filters or search query'}
              </p>
              <div className="flex gap-2 justify-center">
                <Button size="sm" onClick={() => setDialogOpen(true)}>
                  <Plus className="w-3.5 h-3.5 mr-1.5" />
                  Add Source
                </Button>
                <Button size="sm" variant="outline" onClick={() => setActiveTab('browser')}>
                  Find Sources
                </Button>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-border border border-border">
              {filteredSources.map((source) => (
                <SourceCard
                  key={source.id}
                  source={source}
                  onCopyCitation={handleCopyCitation}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Source Browser Tab */}
        <TabsContent value="browser">
          <div className="border border-border mb-6">
            <div className="px-4 py-3 border-b border-border">
              <h3 className="text-sm font-semibold text-foreground">Free Source Browser</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Search OpenLibrary, Semantic Scholar, and arXiv for open-access sources.
              </p>
            </div>
            <div className="p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={browserQuery}
                    onChange={(e) => setBrowserQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleBrowserSearch()}
                    placeholder="Search for books, papers, articles..."
                    className="pl-10"
                    data-testid="browser-search-input"
                  />
                </div>
                <Select value={browserType} onValueChange={setBrowserType}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="book">Books</SelectItem>
                    <SelectItem value="journal">Papers</SelectItem>
                    <SelectItem value="preprint">Preprints</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleBrowserSearch} disabled={browserLoading} data-testid="browser-search-btn">
                  {browserLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
                </Button>
              </div>
            </div>
          </div>

          {browserResults.length > 0 && (
            <div>
              <p className="label-swiss mb-3">{browserResults.length} results found</p>
              <div className="border border-border divide-y divide-border">
                {browserResults.map((result, index) => (
                  <BrowserResultCard
                    key={`${result.title}-${index}`}
                    result={result}
                    onSave={handleSaveFromBrowser}
                  />
                ))}
              </div>
            </div>
          )}

          {browserResults.length === 0 && browserQuery && !browserLoading && (
            <div className="text-center py-12 text-muted-foreground">
              <Search className="w-8 h-8 mx-auto mb-3 text-muted-foreground/40" />
              <p className="text-sm">No results found. Try different search terms.</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Source Card Component
const SourceCard = ({ source, onCopyCitation }) => {
  const Icon = sourceTypeIcons[source.sourceType] || BookOpen;
  const indicators = source.qualityIndicators || [];

  return (
    <div className="border border-border flex items-start gap-4 px-4 py-3 hover:bg-muted/20 transition-colors group">
      <div className="w-7 h-7 bg-muted flex items-center justify-center shrink-0 mt-0.5">
        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-[9px] font-semibold uppercase tracking-[0.1em] text-muted-foreground border border-border px-1.5 py-0.5">
            {sourceTypeLabels[source.sourceType]}
          </span>
          {source.isOpenAccess && (
            <span className="text-[9px] font-semibold uppercase tracking-wide text-green-700">Open Access</span>
          )}
          {source.isUsed && (
            <span className="text-[9px] font-semibold uppercase tracking-wide text-primary">Used</span>
          )}
          {source.projectSources?.slice(0, 2).map(({ project }) => (
            <span key={project.id} className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
              {project.type}
            </span>
          ))}
        </div>

        <Link to={`/research/${source.id}`} className="group-hover:text-primary transition-colors">
          <h3 className="text-sm font-medium text-foreground line-clamp-2">{source.title}</h3>
        </Link>

        <p className="text-[11px] text-muted-foreground mt-0.5">
          {source.authors?.length > 0 ? source.authors.join(', ') : 'Unknown author'}
          {source.publishedDate && <span className="font-mono ml-1">{source.publishedDate.split('-')[0]}</span>}
          {source.publisher && ` · ${source.publisher}`}
        </p>

        {indicators.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {indicators.slice(0, 3).map((ind, i) => {
              const IndIcon = qualityIndicatorIcons[ind.type] || Info;
              return (
                <span key={i} className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 ${
                  ind.type === 'warning' ? 'bg-amber-50 text-amber-700' :
                  ind.type === 'success' ? 'bg-green-50 text-green-700' :
                  'bg-muted text-muted-foreground'
                }`}>
                  <IndIcon className="w-2.5 h-2.5" />
                  {ind.message}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 text-xs" data-testid={`cite-${source.id}`}>
              <Copy className="w-3 h-3 mr-1" />
              Cite
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => onCopyCitation(source, 'mla')}>Copy MLA</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onCopyCitation(source, 'apa')}>Copy APA</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onCopyCitation(source, 'chicago')}>Copy Chicago</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {source.canonicalUrl && (
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => window.open(source.canonicalUrl, '_blank')}>
            <ExternalLink className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Browser Result Card
const BrowserResultCard = ({ result, onSave }) => {
  const Icon = sourceTypeIcons[result.sourceType] || BookOpen;

  return (
    <div className="border border-border flex items-start gap-4 px-4 py-3 hover:bg-muted/20 transition-colors">
      <div className="w-7 h-7 bg-muted flex items-center justify-center shrink-0 mt-0.5">
        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-[9px] font-semibold uppercase tracking-[0.1em] text-muted-foreground border border-border px-1.5 py-0.5">
            {sourceTypeLabels[result.sourceType]}
          </span>
          <span className="text-[9px] font-semibold uppercase tracking-wide text-blue-700">{result.source}</span>
          {result.isOpenAccess && (
            <span className="text-[9px] font-semibold uppercase tracking-wide text-green-700">Open Access</span>
          )}
          {result.qualityScore != null && (
            <span className={`text-[9px] font-mono font-semibold ${
              result.qualityScore >= 75 ? 'text-green-700' :
              result.qualityScore >= 50 ? 'text-amber-700' : 'text-muted-foreground'
            }`}>
              Q:{result.qualityScore}%
            </span>
          )}
        </div>

        <h3 className="text-sm font-medium text-foreground line-clamp-2">{result.title}</h3>

        <p className="text-[11px] text-muted-foreground mt-0.5">
          {result.authors?.length > 0 ? result.authors.slice(0, 3).join(', ') : 'Unknown author'}
          {result.authors?.length > 3 && ' et al.'}
          {result.publishedDate && <span className="font-mono ml-1">{result.publishedDate}</span>}
        </p>

        {result.credibility && (
          <span className="inline-block mt-1.5 text-[10px] px-1.5 py-0.5 bg-muted text-muted-foreground uppercase tracking-wide font-semibold">
            {result.credibility}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1.5 shrink-0">
        <Button size="sm" className="h-7 text-xs" onClick={() => onSave(result)} data-testid={`save-${result.title?.slice(0,10)}`}>
          <Download className="w-3 h-3 mr-1" />
          Save
        </Button>
        {result.canonicalUrl && (
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => window.open(result.canonicalUrl, '_blank')}>
            <Eye className="w-3 h-3 mr-1" />
            Preview
          </Button>
        )}
      </div>
    </div>
  );
};

export default ResearchPage;
